import { BuildRCAttribute } from "@/common/function/RCAttribute";
import Canvas from "@/components/ui/Canvas";
import { ICanvasProps } from "@/components/ui/Canvas/Models";
import {
  ListResourceIcon,
  RenderListIcon,
  RenderMapIcon,
} from "@/components/ui/Icons/IconsCustom";
import { RootState } from "@/redux";
import { IOrganizationStore } from "@/redux/slices/organizations/OrganizationModel";
import { EFieldFilter } from "@/view/addin/resource-list/filter/Models";
import ResourceSelected from "@/view/addin/selected-resources/ResourceSelectedContainer";
import {
  SelectTabData,
  SelectTabEvent,
  Tab,
  TabList,
} from "@fluentui/react-components";
import { cloneDeep } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ETabList } from "./ResourceListModel";
import {
  DivResourceListWrapper,
  DivTabWrapper,
  HeaderTitleWrapper,
  ResourceTabBaseWrapper,
} from "./Styles";
import Filter from "./filter/FilterContainer";
import ResourceFloorPlan from "./floor-plan/ResourceFloorPlanContainer";
import ResourceList from "./list/ListContainer";

const ResourceTab = () => {
  const { t } = useTranslation();
  const theme: string = useSelector(
    (state: RootState) => state.settingInfo?.theme
  ) as string;
  const orgStore: IOrganizationStore = useSelector(
    (state: RootState) => state.orgInfo
  ) as IOrganizationStore;

  const [selectedTab, setSelectedTab] = useState<ETabList>(ETabList.LIST);
  const resourceDivRef = useRef(null);
  const resourceSelectedRef = useRef(null);
  const resourceListRef = useRef(null);
  const [heightResourceList, setHeightResourceList] = useState<number>(0);
  const [heightSelectedResourceList, setHeightSelectedResourceList] =
    useState(175);
  const [isOpenResourceList, setIsOpenResourceList] = useState<boolean>(true);
  const [totalResourceList, setTotalResourceList] = useState<{
    quantity: number;
    isLastPage: boolean;
  }>({ quantity: 0, isLastPage: true });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const div = resourceSelectedRef?.current;
    if (!div) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (resourceDivRef.current) {
          const height = (resourceDivRef.current as HTMLElement).offsetHeight;
          const cal = height - 58 - entry.contentRect.height + 23;
          setHeightResourceList(cal);
        }
      }
    });
    observer.observe(div);
    return () => {
      observer.unobserve(div);
    };
  }, [resourceSelectedRef]);

  const setMaxHeightSelectedResource = (
    isOpen: boolean,
    quantity?: number
  ): void => {
    if (!isOpen) {
      const div: HTMLElement | null = resourceListRef?.current || null;
      if (!div) {
        return;
      }
      const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        for (const entry of entries) {
          if (
            resourceDivRef.current &&
            (div as HTMLElement)?.offsetHeight === 0
          ) {
            const height: number = (resourceDivRef.current as HTMLElement)
              ?.offsetHeight;
            const cal: number = height - 160 + 48;
            setHeightSelectedResourceList(cal - (quantity || 0));
          }
        }
      });
      observer.observe(div);
    } else {
      setHeightSelectedResourceList(0);
    }
  };

  const renderTotalResourceList = (): string => {
    if (
      totalResourceList.quantity &&
      selectedTab === ETabList.LIST &&
      !loading
    ) {
      return `(${totalResourceList.quantity}${
        totalResourceList.isLastPage ? "" : "+"
      })`;
    }
    return "";
  };

  const updateLoading = (loadingClone: boolean) => {
    setLoading(loadingClone);
  };

  const args: ICanvasProps = {
    headerTitle: (
      <HeaderTitleWrapper className="title-select HeaderTitleWrapper">
        <ListResourceIcon theme={theme} />
        <span>{`${t("Resources List")} ${renderTotalResourceList()}`}</span>
      </HeaderTitleWrapper>
    ),
    isStickHeader: false,
    isOpen: isOpenResourceList,
    onOpen: (isOpen: boolean) => {
      setMaxHeightSelectedResource(isOpen);
      setIsOpenResourceList(isOpen);
    },
  };

  const onTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
    event.preventDefault();
    setSelectedTab(data.value as ETabList);
    if (data.value === ETabList.FLOOR_PLAN) {
      setTotalResourceList({ quantity: 0, isLastPage: true });
    }
  };

  const onChangeTotalResourceList = (totalList: {
    quantity: number;
    isLastPage: boolean;
  }) => {
    setTotalResourceList(cloneDeep(totalList));
  };

  return (
    <ResourceTabBaseWrapper
      className="ResourceTabBaseWrapper"
      ref={resourceDivRef}
    >
      {/* {isLoading ? <LoadingCustom /> : null} */}
      <div ref={resourceSelectedRef}>
        <ResourceSelected maxHeight={heightSelectedResourceList} />
      </div>
      <Canvas {...args} theme={theme} rcName="resource_list">
        <DivResourceListWrapper
          ref={resourceListRef}
          className="DivResourceListWrapper"
          $height={heightResourceList}
        >
          <Filter
            hiddenField={
              selectedTab === ETabList.FLOOR_PLAN
                ? [EFieldFilter.Search]
                : undefined
            }
          />
          <TabList
            selectedValue={selectedTab}
            onTabSelect={onTabSelect}
            className="custom_tablist"
            {...BuildRCAttribute(`resource_tab_list`)}
          >
            <DivTabWrapper className="DivTabWrapper">
              <Tab
                value={ETabList.LIST}
                icon={
                  <RenderListIcon
                    theme={theme}
                    isActive={selectedTab === ETabList.LIST}
                  />
                }
                className="tab"
                {...BuildRCAttribute(`resource_tab_item.${ETabList.LIST}`)}
              >
                {t(ETabList.LIST)}
              </Tab>
            </DivTabWrapper>
            <DivTabWrapper className="DivTabWrapper">
              <Tab
                value={ETabList.FLOOR_PLAN}
                icon={
                  <RenderMapIcon
                    theme={theme}
                    isActive={selectedTab === ETabList.FLOOR_PLAN}
                  />
                }
                className="tab"
                {...BuildRCAttribute(
                  `resource_tab_item.${ETabList.FLOOR_PLAN}`
                )}
              >
                {t(ETabList.FLOOR_PLAN)}
              </Tab>
            </DivTabWrapper>
          </TabList>
          {selectedTab === ETabList.LIST ? (
            <ResourceList
              maxHeight={
                heightResourceList -
                (!orgStore?.outlookInfo?.isRecurringMaster ? 198 : 157)
              }
              onChangeTotalResourceList={onChangeTotalResourceList}
              updateLoading={updateLoading}
            />
          ) : (
            <ResourceFloorPlan maxHeight={heightResourceList - 199} />
          )}
        </DivResourceListWrapper>
      </Canvas>
    </ResourceTabBaseWrapper>
  );
};

export default ResourceTab;
